import { StarFilled } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Alert, Badge, Button, Image, Rate, Switch, Tag, Tooltip } from 'antd';
import { filter, map, size } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as PlayIcon } from '../../../../../assets/svg/play-solid.svg';
import {
  DATE_FORMATS,
  REQUEST_STATUS,
  ROUTES,
} from '../../../../../common/constants';
import { formatDate } from '../../../../../common/utils';
import ModalComponent from '../../../../../components/ModalComponent';
import RenderCopyIcon from '../../../../../components/RenderCopyIcon';
import ToyVideoModal from '../../../../../libraryModules/raiseRequest/components/ToyVideoModal';
import { TOY_FOR_REVIEW_REQUEST } from '../graphql/Queries';
import {
  formatAgeGroup,
  formatEngagementDuration,
  getMediaId,
  getReviewComparison,
  getTagId,
  isVideoMedia,
  REVIEW_CHANGE_STATUS,
  REVIEW_COMPARE_FIELDS,
  REVIEW_COMPARE_FIELD_LABELS,
  sortByOrder,
} from '../reviewCompareUtils';

const EMPTY_VALUE = <span className="review-compare-empty">Not provided</span>;

const REMOVED_VALUE = (
  <span className="review-compare-text">Removed</span>
);

const renderRatingValue = (rating) => {
  if (rating == null) {
    return EMPTY_VALUE;
  }

  return (
    <span className="review-compare-rating">
      <Rate
        disabled
        allowHalf
        value={rating}
        className="review-compare-stars"
      />
      <span className="review-compare-rating-value">{rating}</span>
    </span>
  );
};

const renderTagList = (tagList, isRemoved = false) => {
  if (!size(tagList)) {
    return isRemoved ? REMOVED_VALUE : EMPTY_VALUE;
  }

  return (
    <div className="review-compare-tags">
      {map(tagList, (tag) => {
        const isAdded =
          tag?.changeStatus === REVIEW_CHANGE_STATUS?.ADDED;

        return (
          <Tag
            key={getTagId(tag)}
            className={`review-compare-tag ${
              isAdded ? 'review-compare-tag-added' : ''
            }`}
          >
            {tag?.label || tag?.itemKey}
          </Tag>
        );
      })}
    </div>
  );
};

const renderDiffContent = (tokens, plainText, isRemoved = false) => {
  if (isRemoved) {
    return REMOVED_VALUE;
  }

  if (!size(tokens)) {
    return plainText ? (
      <span className="review-compare-text">{plainText}</span>
    ) : (
      EMPTY_VALUE
    );
  }

  return (
    <span className="review-compare-text">
      {map(tokens, (token, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={`${index}-${token?.type}`}
          className={`review-compare-token review-compare-token-${token?.type?.toLowerCase()}`}
        >
          {token?.value}
        </span>
      ))}
    </span>
  );
};

const renderPlainValue = (value, isRemoved = false) => {
  if (isRemoved) {
    return REMOVED_VALUE;
  }

  return value ? (
    <span className="review-compare-text">{value}</span>
  ) : (
    EMPTY_VALUE
  );
};

const CompareRow = ({ label, changed, previousNode, updatedNode }) => (
  <div
    className={`review-compare-row ${
      changed ? 'review-compare-row-changed' : ''
    }`}
  >
    <div className="review-compare-row-head">
      <span className="review-compare-row-label">{label}</span>

      {changed ? (
        <Tag className="review-compare-changed-tag">Changed</Tag>
      ) : (
        <span className="review-compare-unchanged-label">No change</span>
      )}
    </div>

    <div className="review-compare-row-body">
      <div className="review-compare-cell review-compare-cell-previous">
        {previousNode}
      </div>

      <div className="review-compare-cell review-compare-cell-updated">
        {updatedNode}
      </div>
    </div>
  </div>
);

const ReviewCompareModal = ({
  open,
  record,
  isArchivedView = false,
  onClose,
  onApprove,
  onReject,
  onArchive,
}) => {
  const {
    state: {
      commonPermissions: { isToyPermission },
    },
  } = useContext(AppContext);

  const [onlyChanges, setOnlyChanges] = useState(true);
  const [toyDetails, setToyDetails] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const [toyAdmin] = useLazyQuery(TOY_FOR_REVIEW_REQUEST, {
    onCompleted: (response) => {
      setToyDetails(response?.toyAdmin);
    },
    fetchPolicy: 'cache-first',
    onError() {},
  });

  const comparison = useMemo(() => getReviewComparison(record), [record]);
  const userData = record?.createdBy;
  const toyId = record?.metaData?.toyId;
  const isPending = record?.status === REQUEST_STATUS?.PENDING?.value;

  useEffect(() => {
    if (open && toyId && isToyPermission) {
      setToyDetails(null);
      toyAdmin({ variables: { where: { id: toyId } } });
    }

    if (!open) {
      setOnlyChanges(true);
      setVideoUrl('');
      setIsVideoModalOpen(false);
    }
  }, [open, toyId]);

  const renderMediaList = (
    mediaList,
    isPreviousSide,
    isRemoved = false,
  ) => {
    if (!size(mediaList)) {
      return isRemoved ? REMOVED_VALUE : (
        <span className="review-compare-empty">No media</span>
      );
    }

    return (
      <div className="review-compare-media">
        <Image.PreviewGroup>
          {map(mediaList, (media) => {
            const isVideo = isVideoMedia(media);
            const isAdded =
              media?.changeStatus === REVIEW_CHANGE_STATUS?.ADDED;

            return (
              <div
                key={`${isPreviousSide ? 'prev' : 'new'}-${getMediaId(media)}`}
                className={`review-compare-media-item ${
                  isAdded ? 'review-compare-media-item-added' : ''
                }`}
              >
                {isVideo ? (
                  <Tooltip title="Play video">
                    <button
                      type="button"
                      className="review-compare-media-video"
                      onClick={() => {
                        setVideoUrl(media?.url);
                        setIsVideoModalOpen(true);
                      }}
                    >
                      <PlayIcon className="review-compare-media-play" />
                      <span className="review-compare-media-video-label">
                        Video
                      </span>
                    </button>
                  </Tooltip>
                ) : (
                  <Image
                    src={media?.url}
                    alt="Review media"
                    className="review-compare-media-image"
                    rootClassName="review-compare-media-image-root"
                  />
                )}

                {isAdded ? (
                  <span className="review-compare-media-badge review-compare-media-badge-added">
                    New
                  </span>
                ) : null}
              </div>
            );
          })}
        </Image.PreviewGroup>
      </div>
    );
  };

  const isRatingRemoved =
    comparison?.rating?.previous != null &&
    comparison?.rating?.updated == null;

  const isContentRemoved =
    !!comparison?.content?.previous &&
    !comparison?.content?.updated;

  const isTagsRemoved =
    size(comparison?.tags?.previous) > 0 &&
    size(comparison?.tags?.updated) === 0;

  const isMediaRemoved =
    size(comparison?.media?.previous) > 0 &&
    size(comparison?.media?.updated) === 0;

  const isEngagementDurationRemoved =
    !!comparison?.engagementDuration?.previous &&
    !comparison?.engagementDuration?.updated;

  const isAgeGroupRemoved =
    !!comparison?.ageGroup?.previous &&
    !comparison?.ageGroup?.updated;

  const rows = [
    {
      key: REVIEW_COMPARE_FIELDS?.RATING,
      label: REVIEW_COMPARE_FIELD_LABELS?.RATING,
      changed: comparison?.rating?.changed,
      previousNode: renderRatingValue(comparison?.rating?.previous),
      updatedNode: isRatingRemoved
        ? REMOVED_VALUE
        : renderRatingValue(comparison?.rating?.updated),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.CONTENT,
      label: REVIEW_COMPARE_FIELD_LABELS?.CONTENT,
      changed: comparison?.content?.changed,
      previousNode: renderDiffContent(
        comparison?.contentDiff?.previous,
        comparison?.content?.previous,
      ),
      updatedNode: renderDiffContent(
        comparison?.contentDiff?.updated,
        comparison?.content?.updated,
        isContentRemoved,
      ),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.MEDIA,
      label: `${REVIEW_COMPARE_FIELD_LABELS?.MEDIA} (${size(
        comparison?.media?.previous,
      )} → ${size(comparison?.media?.updated)})`,
      changed: comparison?.media?.changed,
      previousNode: renderMediaList(
        comparison?.media?.previous,
        true,
      ),
      updatedNode: renderMediaList(
        comparison?.media?.updated,
        false,
        isMediaRemoved,
      ),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.TAGS,
      label: REVIEW_COMPARE_FIELD_LABELS?.TAGS,
      changed: comparison?.tags?.changed,
      previousNode: renderTagList(comparison?.tags?.previous),
      updatedNode: renderTagList(
        comparison?.tags?.updated,
        isTagsRemoved,
      ),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.ENGAGEMENT_DURATION,
      label: REVIEW_COMPARE_FIELD_LABELS?.ENGAGEMENT_DURATION,
      changed: comparison?.engagementDuration?.changed,
      previousNode: renderPlainValue(
        formatEngagementDuration(
          comparison?.engagementDuration?.previous,
        ),
      ),
      updatedNode: renderPlainValue(
        formatEngagementDuration(
          comparison?.engagementDuration?.updated,
        ),
        isEngagementDurationRemoved,
      ),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.AGE_GROUP,
      label: REVIEW_COMPARE_FIELD_LABELS?.AGE_GROUP,
      changed: comparison?.ageGroup?.changed,
      previousNode: renderPlainValue(
        formatAgeGroup(comparison?.ageGroup?.previous),
      ),
      updatedNode: renderPlainValue(
        formatAgeGroup(comparison?.ageGroup?.updated),
        isAgeGroupRemoved,
      ),
    },
    {
      key: REVIEW_COMPARE_FIELDS?.IS_AI_GENERATED,
      label: REVIEW_COMPARE_FIELD_LABELS?.IS_AI_GENERATED,
      changed: comparison?.isAiGenerated?.changed,
      previousNode: renderPlainValue(
        comparison?.isAiGenerated?.previous ? 'Yes' : 'No',
      ),
      updatedNode: renderPlainValue(
        comparison?.isAiGenerated?.updated ? 'Yes' : 'No',
      ),
    },
  ];

  const visibleRows = onlyChanges ? filter(rows, 'changed') : rows;
  const toyImage = sortByOrder(toyDetails?.images)?.[0]?.url;

  return (
    <>
      <ToyVideoModal
        isModalOpen={isVideoModalOpen}
        setIsModalOpen={setIsVideoModalOpen}
        videoUrl={videoUrl}
        title="Review Video"
        onCancel={() => setVideoUrl('')}
      />

      <ModalComponent
        open={open}
        onCancel={onClose}
        width={1040}
        destroyOnClose
        className="modal review-compare-modal"
        title="Review Difference"
        footer={
          <div className="review-compare-footer">
            <Button onClick={onClose}>Close</Button>

            <Button
              onClick={() => {
                onClose();
                onArchive(record);
              }}
            >
              {isArchivedView || record?.isArchived
                ? 'Unarchive'
                : 'Archive'}
            </Button>

            {!isArchivedView ? (
              <>
                <Button
                  danger
                  disabled={!isPending}
                  onClick={() => {
                    onClose();
                    onReject(record);
                  }}
                >
                  Reject
                </Button>

                <Button
                  type="primary"
                  className="common-button"
                  disabled={!isPending}
                  onClick={() => {
                    onClose();
                    onApprove(record);
                  }}
                >
                  Approve
                </Button>
              </>
            ) : null}
          </div>
        }
      >
        <div className="review-compare">
          <div className="review-compare-meta">
            <div className="review-compare-meta-item">
              <span className="review-compare-meta-label">User</span>

              <span className="review-compare-meta-value">
                {userData?.firstName || userData?.lastName
                  ? `${userData?.firstName || ''} ${
                      userData?.lastName || ''
                    }`.trim()
                  : '-'}

                {userData?.userNumber ? (
                  <span className="review-compare-meta-sub">
                    {` (${userData?.userNumber})`}
                  </span>
                ) : null}
              </span>
            </div>

            <div className="review-compare-meta-item">
              <span className="review-compare-meta-label">
                Request ID
              </span>

              <span className="review-compare-meta-value d-flex align-center">
                <RenderCopyIcon
                  copyText={record?.requestNumber}
                  toolTipText="Copy Request ID"
                />
                {record?.requestNumber || '-'}
              </span>
            </div>

            <div className="review-compare-meta-item">
              <span className="review-compare-meta-label">
                Requested on
              </span>

              <span className="review-compare-meta-value">
                {formatDate(
                  record?.createdAt,
                  DATE_FORMATS?.REGULAR,
                ) || '-'}
              </span>
            </div>

            <div className="review-compare-meta-item">
              <span className="review-compare-meta-label">
                Status
              </span>

              <span className="review-compare-meta-value">
                <Badge
                  color={REQUEST_STATUS?.[record?.status]?.color}
                  text={REQUEST_STATUS?.[record?.status]?.text}
                />
              </span>
            </div>
          </div>

          {toyDetails ? (
            <div className="review-compare-toy">
              {toyImage ? (
                <img
                  src={toyImage}
                  alt={toyDetails?.name}
                  className="review-compare-toy-image"
                />
              ) : null}

              <div className="review-compare-toy-info">
                <span className="review-compare-meta-label">
                  Reviewed toy
                </span>

                <span className="review-compare-toy-name">
                  {toyDetails?.name || '-'}
                </span>

                {toyDetails?.toyNumber ? (
                  <span className="review-compare-meta-sub">
                    {toyDetails?.toyNumber}
                  </span>
                ) : null}
              </div>

              <Button
                type="link"
                className="review-compare-toy-link"
                onClick={() =>
                  // eslint-disable-next-line no-undef
                  window?.open(
                    `${ROUTES?.MASTER_LISTS}${ROUTES?.TOY}/view/${toyDetails?.id}`,
                    '_blank',
                  )
                }
              >
                View toy
              </Button>
            </div>
          ) : null}

          {!comparison?.hasPreviousReview ? (
            <Alert
              className="mb-16"
              type="info"
              showIcon
              message="No earlier version of this review was found, everything on the right side is new."
            />
          ) : null}

          <div className="review-compare-summary">
            <div className="review-compare-summary-changes">
              <span className="review-compare-meta-label">
                Changed fields
              </span>

              {size(comparison?.changedFields) ? (
                <div className="review-compare-summary-tags">
                  {map(
                    comparison?.changedFields,
                    (field) => (
                      <Tag
                        key={field}
                        className="review-compare-changed-tag"
                      >
                        {field === REVIEW_COMPARE_FIELDS?.MEDIA
                          ? `${REVIEW_COMPARE_FIELD_LABELS?.MEDIA} (+${comparison?.media?.addedCount} / -${comparison?.media?.removedCount})`
                          : REVIEW_COMPARE_FIELD_LABELS?.[field]}
                      </Tag>
                    ),
                  )}
                </div>
              ) : (
                <span className="review-compare-empty">
                  No difference found between the two versions
                </span>
              )}
            </div>

            <div className="review-compare-summary-toggle">
              <span className="mr-8">Only changes</span>

              <Switch
                size="small"
                checked={onlyChanges}
                onChange={(checked) => setOnlyChanges(checked)}
              />
            </div>
          </div>

          <div className="review-compare-columns-head">
            <div className="review-compare-column-head">
              Old review (live on app)
            </div>

            <div className="review-compare-column-head review-compare-column-head-updated">
              Updated review (requested)
            </div>
          </div>

          {size(visibleRows) ? (
            <div className="review-compare-rows">
              {map(visibleRows, (row) => (
                <CompareRow
                  key={row?.key}
                  label={row?.label}
                  changed={row?.changed}
                  previousNode={row?.previousNode}
                  updatedNode={row?.updatedNode}
                />
              ))}
            </div>
          ) : null}

          {!size(visibleRows) ? (
            <div className="review-compare-empty-rows">
              <StarFilled className="rating-star-icon mr-8" />
              Nothing changed in this request, turn off &quot;Only
              changes&quot; to see the full review.
            </div>
          ) : null}
        </div>
      </ModalComponent>
    </>
  );
};

export default ReviewCompareModal;